import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Text } from '../../components/ui'
import type { SupportCenterProfileOption } from '../../types/supportCenterSpecialties'
import { toggleSortedNumber } from '../../components/centralAmparo/scheduleHelpers'
import { DatePartSelectors } from './ParametrosFormFields'
import type { DateParts } from '../../components/centralAmparo/parametrosHelpers'

export type UnavailModalState = {
  id?: string
  user_id: string
  start: DateParts
  end: DateParts
  /** Quando true, exibe e exige data final (período). */
  spanDays: boolean
  all_day: boolean
  hours: number[]
  reason: string
}

type ParametrosUnavailModalProps = {
  unavailModal: UnavailModalState
  unavailError: string | null
  submitting: boolean
  assignedProfileOptions: SupportCenterProfileOption[]
  /** Trava o select no profissional já preenchido (ex.: agenda própria). */
  lockProfessional?: boolean
  onChange: (next: UnavailModalState) => void
  onClose: () => void
  onSave: () => void
}

const ParametrosUnavailModal: React.FC<ParametrosUnavailModalProps> = ({
  unavailModal,
  unavailError,
  submitting,
  assignedProfileOptions,
  lockProfessional = false,
  onChange,
  onClose,
  onSave,
}) => {
  const profileOptions = React.useMemo(() => {
    if (
      unavailModal.user_id &&
      !assignedProfileOptions.some((p) => p.id === unavailModal.user_id)
    ) {
      return [
        ...assignedProfileOptions,
        { id: unavailModal.user_id, name: 'Profissional selecionado' },
      ]
    }
    return assignedProfileOptions
  }, [assignedProfileOptions, unavailModal.user_id])

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
            {unavailModal.id ? 'Editar Indisponibilidade' : 'Nova Indisponibilidade'}
          </Text>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Fechar
          </Button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Profissional <span className="text-red-500">*</span>
            </label>
            <select
              value={unavailModal.user_id}
              onChange={(e) => onChange({ ...unavailModal, user_id: e.target.value })}
              disabled={lockProfessional || submitting}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {!lockProfessional && <option value="">Selecione...</option>}
              {profileOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              id="unavail-span-days"
              type="checkbox"
              checked={unavailModal.spanDays}
              disabled={submitting}
              onChange={(e) => {
                const spanDays = e.target.checked
                onChange({
                  ...unavailModal,
                  spanDays,
                  end: spanDays ? unavailModal.end : { ...unavailModal.start },
                })
              }}
            />
            <label htmlFor="unavail-span-days" className="text-sm text-gray-700 dark:text-gray-300">
              Vários dias
            </label>
          </div>
          <DatePartSelectors
            label={
              unavailModal.spanDays ? 'Início (dia / mês / ano)' : 'Data (dia / mês / ano)'
            }
            parts={unavailModal.start}
            onChange={(next) =>
              onChange({
                ...unavailModal,
                start: next,
                end: unavailModal.spanDays ? unavailModal.end : { ...next },
              })
            }
          />
          {unavailModal.spanDays && (
            <DatePartSelectors
              label="Fim (dia / mês / ano)"
              parts={unavailModal.end}
              onChange={(next) => onChange({ ...unavailModal, end: next })}
            />
          )}
          <div className="flex items-center gap-2">
            <input
              id="unavail-all-day"
              type="checkbox"
              checked={unavailModal.all_day}
              disabled={submitting}
              onChange={(e) =>
                onChange({
                  ...unavailModal,
                  all_day: e.target.checked,
                  hours: e.target.checked ? [] : unavailModal.hours,
                })
              }
            />
            <label htmlFor="unavail-all-day" className="text-sm text-gray-700 dark:text-gray-300">
              Dia(s) inteiro(s)
            </label>
          </div>
          {!unavailModal.all_day && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Horas indisponíveis <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={submitting}
                    onClick={() =>
                      onChange({
                        ...unavailModal,
                        hours: Array.from({ length: 24 }, (_, i) => i),
                      })
                    }
                  >
                    Selecionar todos
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={submitting}
                    onClick={() => onChange({ ...unavailModal, hours: [] })}
                  >
                    Limpar
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
                {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                  <label
                    key={h}
                    className={`inline-flex items-center justify-center gap-2 px-2 py-1.5 rounded-md border text-sm cursor-pointer select-none ${
                      unavailModal.hours.includes(h)
                        ? 'border-primary-500 text-primary-700 dark:text-primary-300'
                        : 'border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={unavailModal.hours.includes(h)}
                      disabled={submitting}
                      onChange={() =>
                        onChange({
                          ...unavailModal,
                          hours: toggleSortedNumber(unavailModal.hours, h),
                        })
                      }
                    />
                    {h.toString().padStart(2, '0')}h
                  </label>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Motivo
            </label>
            <input
              type="text"
              value={unavailModal.reason}
              disabled={submitting}
              onChange={(e) => onChange({ ...unavailModal, reason: e.target.value })}
              placeholder="Ex: Férias, folga..."
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-70"
            />
          </div>
        </div>
        {unavailError && (
          <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            <Text size="sm">{unavailError}</Text>
          </div>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="solid" onClick={onSave} disabled={submitting}>
            {submitting ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default ParametrosUnavailModal
